import { fetchTranslationFile } from 'features/i18n/DynamicTranslation/fetchTranslationFile';
import { logger } from 'features/logger/logger';
import { preloadParticipationAssets } from 'features/participations/schedule/services/preloadParticipationAssets';
import {
  getAssetUrlWithTempLocationSupport,
  questionnaireAssetApi,
} from 'features/questionnaires/store/questionnaireAssetApiSlice';
import { readBodyOrThrow } from 'shared/utils/readBodyOrThrow';
import { createUrlWarmer } from 'shared/utils/urlWarmer';
import { urlWithoutQuery } from 'shared/utils/urlWithoutQuery';
import { getProtocolManifest, PREFETCH_TIMEOUT_MS } from './protocolManifest';
import type { DbProtocolManifest } from '@hub/common';
import type { AppDispatch } from 'features/store/store';

const preloadStylesheet = (style: string): void => {
  const href = getAssetUrlWithTempLocationSupport(style);
  if (
    document.head.querySelector(
      `link[rel="preload"][as="style"][href="${CSS.escape(href)}"]`
    )
  )
    return;
  const link = document.createElement('link');
  link.setAttribute('rel', 'preload');
  link.setAttribute('as', 'style');
  link.setAttribute('href', href);
  link.setAttribute('fetchpriority', 'low');
  document.head.appendChild(link);
};

const translations = createUrlWarmer();

const prefetchTranslation = (
  protocolId: string,
  language: string,
  manifest: DbProtocolManifest
): void => {
  const urlFromManifest = manifest.translations[language];
  if (!urlFromManifest) return;
  const url = getAssetUrlWithTempLocationSupport(urlFromManifest);
  translations.warm(
    url,
    async () => {
      const response = await fetchTranslationFile(url, {
        signal: AbortSignal.timeout(PREFETCH_TIMEOUT_MS),
      });
      await readBodyOrThrow(response, 'Translation');
    },
    error => {
      logger.warn('Unable to prefetch the translation file', {
        details: { protocolId, language, url: urlWithoutQuery(url), error },
      });
    }
  );
};

export const forgetPrefetchedTranslations = (): void => {
  translations.forget();
};

/** Loads the protocol's manifest, then starts loading its DSL into the
 *  questionnaire asset cache, its images, its stylesheet and, for a known
 *  `language` the manifest lists, its translation file. `language` is the key
 *  the dynamic i18next instance will read. Never rejects. */
export const prefetchStartAssets = async (
  protocolId: string,
  dispatch: AppDispatch,
  language: string | undefined
): Promise<void> => {
  const manifest = await getProtocolManifest(protocolId);
  if (manifest === null) return;
  dispatch(
    questionnaireAssetApi.util.prefetch(
      'getProseDslSourceByAssetId',
      manifest.definition,
      {}
    )
  );
  preloadParticipationAssets(manifest);
  if (manifest.style !== undefined) preloadStylesheet(manifest.style);
  if (language !== undefined)
    prefetchTranslation(protocolId, language, manifest);
};
